import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/context/AuthContext';
import { usePortfolioView } from '../context/PortfolioViewContext';
import { parseLocalDate, getWeekStartMonday, getWindowStartDate } from '../../../shared/utils/dateHelpers';
import { getTickerView } from '../../../shared/utils/chartToggles';

// Import card components
import {
  PortfolioSummaryCard,
  TopMoversCard,
  PerformanceChartCard,
  InvestmentTableCard,
} from '../components/cards';

// Import skeleton and error components
import { PortfolioPageSkeleton } from '../components/PortfolioPageSkeleton';
import { ReconnectingChip } from '../../../shared/components/ui/ReconnectingChip';

// Import DashboardContext for data
import { useDashboard } from '../context/DashboardContext';
import {
  selectPortfolioChart,
  selectAccountChart,
  selectActivityEvents,
  type TickerCostBasisPoint,
} from '../state/dashboardSelectors';
import type { TimeWindow } from '../services/dashboardApi';
import { useTickerChart, useUserTickerChart } from '../hooks/useTickerChart';
import { getTickerMarket } from '../services/tickerApi';

// Import trading activities
import { TradingActivity } from '../types/tradingActivity';
import TradingActivityModal from '../../../shared/charts/TradingActivityModal';

// Import custom hooks for modal and CRUD operations
import {
  usePortfolioModals,
  useAccountManagement,
  useTransactionManagement,
  useAccount,
  useAccountTransactions,
} from '../hooks';
import { getFormDraftStorageKey } from '../hooks/useFormDraftPersistence';
import type { PortfolioModalView } from '../hooks/usePortfolioModals';
import type { TransactionSubmitResult } from '../hooks/useTransactionManagement';

// Import modal components
import Modal from '../../../shared/components/ui/Modal';
import AccountList from '../components/modals/AccountList';
import AddAccountType from '../components/modals/AddAccountType';
import AddManualAccountForm from '../components/modals/AddManualAccountForm';
import AddTransactionOptions from '../components/modals/AddTransactionOptions';
import ManualTransactionForm, { type ManualTransactionFormData } from '../components/modals/ManualTransactionForm';
import EditAccountTransactions, { clearAccountTransactionFilters } from '../components/modals/EditAccountTransactions';
import BrokerCsvUploadModal from '../components/modals/BrokerCsvUploadModal';
import DefaultCsvUploadModal from '../components/modals/DefaultCsvUploadModal';
import LinkBrokerageModal from '../components/modals/LinkBrokerageModal';
import { unlinkBrokerageAccount } from '../services/brokerageApi';

// Import types
import { InvestmentAccount } from '../types';

// Import constants
import { getTierVisibilityConfig, type PortfolioViewTier } from '../../../shared/utils/tierVisibility';
import { loadPortfolioPrefs } from '../../../shared/utils/portfolioPrefs';

// LocalStorage keys
const LOCALSTORAGE_SELECTED_TICKER = 'dashboard_selectedTicker';
const LOCALSTORAGE_SELECTED_ACCOUNT_ID = 'dashboard_selectedAccountId';
const LOCALSTORAGE_SELECTED_TIME_WINDOW = 'dashboard_selectedTimeWindowKey';
const SNAPTRADE_LINK_FLOW_STORAGE_KEY = 'vester:snaptrade-link-flow';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert Redis activities format to hook format
 * Redis: {accountId: {ticker: events[]}}
 * Hook: TradingActivitiesData = {date: {hasEntry, hasExit, activities: TradingActivity[]}}
 * 
 * This mirrors the logic in useTradingActivities hook
 */
function convertRedisActivitiesToHookFormat(
  redisActivities: any,
  accountId: string | null,
  ticker: string | null,
  timeWindow: string
): any {
  const result: any = {};
  
  if (!redisActivities || typeof redisActivities !== 'object') return result;
  
  // Helper for weekly bucketing (same as useTradingActivities)
  const toWeekBucketIso = (tsMs: number): string => {
    const mondayMs = getWeekStartMonday(tsMs);
    const monday20UTC = new Date(mondayMs);
    monday20UTC.setUTCHours(20, 0, 0, 0);
    return monday20UTC.toISOString().split('T')[0];
  };
  
  // Filter by window start date
  const windowStart = getWindowStartDate(timeWindow).getTime();
  
  const allActivities: any[] = [];
  
  // Filter by account and/or ticker if specified
  const accountIds = accountId ? [accountId] : Object.keys(redisActivities);
  
  for (const acctId of accountIds) {
    const accountEvents = redisActivities[acctId];
    if (!accountEvents || typeof accountEvents !== 'object') continue;
    
    const tickers = ticker ? [ticker.toUpperCase()] : Object.keys(accountEvents);
    
    for (const tkr of tickers) {
      const events = accountEvents[tkr];
      if (!Array.isArray(events)) continue;
      
      for (const ev of events) {
        // Parse date (same logic as useTradingActivities)
        let ms: number;
        if (typeof ev.date === 'number') {
          ms = ev.date < 1e12 ? ev.date * 1000 : ev.date;
        } else if (typeof ev.date === 'string') {
          ms = parseLocalDate(ev.date);
        } else if (ev.timestamp) {
          ms = typeof ev.timestamp === 'number' 
            ? (ev.timestamp < 1e12 ? ev.timestamp * 1000 : ev.timestamp)
            : new Date(ev.timestamp).getTime();
        } else {
          continue;
        }
        
        if (!Number.isFinite(ms) || ms < windowStart) continue;
        
        const iso = new Date(ms).toISOString();
        const dateOnly = iso.split('T')[0];
        const et = String(ev.event_type || '').toUpperCase();
        const intent = (ev.intent || '').toString();
        const isShort = intent === 'Short';
        
        // Map to TradingActivity format (same as useTradingActivities)
        // Support both new format (long_descr) and old format (description) for backward compatibility
        const activity: any = {
          date: dateOnly,
          timestamp: iso,
          ticker: tkr,
          action: (et === 'SELL' || et === 'BUY_TO_COVER' || et === 'CALL_SELL' || et === 'PUT_SELL') ? 'SELL' : 'BUY',
          quantity: Math.abs(ev.shares || ev.quantity || ev.contracts || 0),
          price: ev.trade_price || ev.price || 0,
          description: ev.long_descr || ev.description || undefined, // Use long_descr (new) or description (old)
          account_id: acctId,
          isShort,
          instrument: (() => {
            const instr = String(ev.instrument || '').toUpperCase();
            if (instr === 'CASH' || instr === 'INTEREST') {
              return instr;
            } else if (instr === 'DIVIDEND') {
              return 'DIVIDEND';
            } else if (instr === 'OPTION' || (et.startsWith('CALL') || et.startsWith('PUT'))) {
              return 'OPTION';
            } else {
              return 'EQUITY';
            }
          })(),
          optionType: et.startsWith('CALL') ? 'CALL' : (et.startsWith('PUT') ? 'PUT' : undefined),
          optionSide: (et.endsWith('SELL') ? 'SELL' : (et.startsWith('PUT') || et.startsWith('CALL') ? 'BUY' : undefined)),
          event_type: et,
          intent: intent,
          split_affected: ev.split_affected === true || ev.split_affected === 'true', // Include split_affected flag from cache
        };
        allActivities.push(activity);
      }
    }
  }
  
  // Group by day (1m/3m/6m) or by week (1y/2y) - same as useTradingActivities
  for (const a of allActivities) {
    const ts = Date.parse(a.timestamp);
    const key = (timeWindow === '1y' || timeWindow === '2y') ? toWeekBucketIso(ts) : a.date;
    if (!result[key]) {
      result[key] = { hasEntry: false, hasExit: false, activities: [] };
    }
    result[key].activities.push(a);
    
    // Compute hasEntry/hasExit (only for EQUITY)
    if (a.instrument === 'EQUITY') {
      const et = (a.event_type || '').toUpperCase();
      if (et === 'BUY' || et === 'SHORT_SELL') result[key].hasEntry = true;
      if (et === 'SELL' || et === 'BUY_TO_COVER') result[key].hasExit = true;
    }
  }
  
  return result;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PortfolioPage: React.FC = () => {
  const { user, profile, session } = useAuth();
  const queryClient = useQueryClient();

  // Context
  const {
    timeWindowOptions,
    selectedTimeWindow,
    setSelectedTimeWindow,
    expandedAccounts,
    toggleAccountExpansion,
  } = usePortfolioView();

  // Track toggle state changes for Redis chart selectors
  const [toggleVersion, setToggleVersion] = useState(0);
  
  useEffect(() => {
    const handleToggleStateChange = () => {
      setToggleVersion((v) => v + 1);
    };
    const handleStorageChange = () => {
      setToggleVersion((v) => v + 1);
    };
    window.addEventListener('toggleStateChanged', handleToggleStateChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('toggleStateChanged', handleToggleStateChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // ========================================================================
  // DASHBOARD DATA (from DashboardContext)
  // ========================================================================

  const dashboardData = useDashboard(); // Get full dashboardData, not just data
  const { data, isLoading, error, refetch } = dashboardData;

  // ========================================================================
  // LOCAL STATE
  // ========================================================================

  // Chart selection state
  const [selectedTicker, setSelectedTicker] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(LOCALSTORAGE_SELECTED_TICKER);
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(LOCALSTORAGE_SELECTED_ACCOUNT_ID);
  });

  // Chart data (computed from Redis selectors)
  const portfolioChart = useMemo(
    () => selectPortfolioChart(data, selectedTimeWindow.key as TimeWindow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selectedTimeWindow.key, toggleVersion]
  );

  const accountChart = useMemo(
    () => selectedAccountId && data
      ? selectAccountChart(data, selectedAccountId, selectedTimeWindow.key as TimeWindow)
      : { series: [], headerPct: null, headerDollar: null, view: 'pct' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selectedAccountId, selectedTimeWindow.key, toggleVersion]
  );

  // Fetch shared ticker chart data (market price series) from lazy endpoint
  const { data: tickerChartResponse, isLoading: isLoadingTickerChart } = useTickerChart(
    selectedTicker,
    selectedTimeWindow.key as TimeWindow
  );
  
  // Fetch ticker market data for the selected ticker (to get company_name for chart title)
  const { data: selectedTickerMarketData } = useQuery({
    queryKey: ['ticker-market', selectedTicker],
    queryFn: async () => {
      if (!selectedTicker || !session?.access_token) {
        return null;
      }
      return getTickerMarket({
        ticker: selectedTicker,
        token: session.access_token,
      });
    },
    enabled: !!selectedTicker && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
  
  // Fetch user-specific ticker chart data (cost basis, PnL) from lazy endpoint
  const { data: userTickerChartResponse, isLoading: isLoadingUserTickerChart } = useUserTickerChart(
    selectedAccountId,
    selectedTicker,
    selectedTimeWindow.key as TimeWindow
  );
  
  // Convert lazy ticker chart data to format expected by chart component
  const tickerChart = useMemo(() => {
    if (!selectedTicker) {
      return { priceSeries: [], name: null };
    }
    
    // If loading, return empty (will show loading state)
    if (isLoadingTickerChart || !tickerChartResponse?.data?.tickerCharts) {
      return { priceSeries: [], name: selectedTicker };
    }
    
    const tickerUpper = selectedTicker.toUpperCase();
    const tickerData = tickerChartResponse.data.tickerCharts[tickerUpper];
    const window = selectedTimeWindow.key as TimeWindow;
    const windowSeries = tickerData?.[window];
    
    if (!windowSeries || !Array.isArray(windowSeries)) {
      return { priceSeries: [], name: selectedTicker };
    }
    
    // Convert to {time, close} format
    // Backend returns {date (epoch ms), price} format
    let priceSeries = windowSeries.map((point: any) => ({
      time: typeof point.date === 'number' ? point.date : (typeof point.date === 'string' ? new Date(point.date).getTime() : 0),
      close: point.price || 0,
    }));
    
    // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
    if ((window === '1y' || window === '2y') && priceSeries.length > 0) {
      const weeklyBuckets = new Map<number, { time: number; close: number; latestTime: number }>();
      for (const pt of priceSeries) {
        const bucket = getWeekStartMonday(pt.time);
        const current = weeklyBuckets.get(bucket);
        if (!current || pt.time > current.latestTime) {
          weeklyBuckets.set(bucket, {
            time: bucket,
            close: pt.close,
            latestTime: pt.time
          });
        }
      }
      priceSeries = Array.from(weeklyBuckets.values())
        .sort((a, b) => a.time - b.time)
        .map(b => ({ time: b.time, close: b.close }));
    }
    
    // Get company name from:
    // 1. Selected ticker market data (if available - most reliable)
    //    Single ticker response: data is TickerMarketData object with company_name directly on it
    // 2. topMovers.tickers (if ticker is in top movers)
    // 3. Fallback to ticker symbol
    const marketData = selectedTickerMarketData?.data as any;
    // Single ticker response structure: data is TickerMarketData object (company_name directly on data)
    const marketCompanyName = marketData?.company_name;
    const topMoversCompanyName = data?.topMovers?.tickers?.[tickerUpper]?.company_name;
    const companyName = marketCompanyName || topMoversCompanyName;
    const name = companyName || selectedTicker;
    
    return { priceSeries, name };
  }, [selectedTicker, tickerChartResponse, isLoadingTickerChart, selectedTimeWindow.key, data, selectedTickerMarketData]);
  
  // Convert user ticker chart data to cost basis format
  const tickerCostBasisData = useMemo(() => {
    if (!selectedAccountId || !selectedTicker) {
      return [];
    }
    
    // If loading, return empty (will show loading state)
    if (isLoadingUserTickerChart || !userTickerChartResponse?.data?.series) {
      return [];
    }
    
    const series = userTickerChartResponse.data.series;
    if (!Array.isArray(series)) {
      return [];
    }
    
    const window = selectedTimeWindow.key as TimeWindow;
    
    // Convert to TickerCostBasisPoint format
    // Backend returns {date (epoch ms), cost_basis, cumulative_dollar_pnl} format
    let costBasisSeries = series.map((point: any) => ({
      time: typeof point.date === 'number' ? point.date : (typeof point.date === 'string' ? new Date(point.date).getTime() : 0),
      basis: point.cost_basis !== undefined ? point.cost_basis : null,
      cumulativeDollarPnl: point.cumulative_dollar_pnl,
    }));
    
    // For 1Y and 2Y, reduce to weekly buckets (Monday labels with latest values)
    if ((window === '1y' || window === '2y') && costBasisSeries.length > 0) {
      const weeklyBuckets = new Map<number, { time: number; basis: number | null; cumulativeDollarPnl?: number; latestTime: number }>();
      for (const pt of costBasisSeries) {
        const bucket = getWeekStartMonday(pt.time);
        const current = weeklyBuckets.get(bucket);
        if (!current || pt.time > current.latestTime) {
          weeklyBuckets.set(bucket, {
            time: bucket,
            basis: pt.basis,
            cumulativeDollarPnl: pt.cumulativeDollarPnl,
            latestTime: pt.time
          });
        }
      }
      costBasisSeries = Array.from(weeklyBuckets.values())
        .sort((a, b) => a.time - b.time)
        .map(b => ({ time: b.time, basis: b.basis, cumulativeDollarPnl: b.cumulativeDollarPnl }));
    }
    
    return costBasisSeries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, selectedTicker, userTickerChartResponse, isLoadingUserTickerChart, selectedTimeWindow.key, toggleVersion]);
  
  // Get ticker view for cost basis (dol1 vs dol2)
  // Must include toggleVersion to react to toggle changes
  const tickerView = useMemo<'dol1' | 'dol2'>(() => {
    if (selectedAccountId && selectedTicker) {
      const view = getTickerView(selectedAccountId, selectedTicker);
      return view === 'dol2' ? 'dol2' : 'dol1';
    }
    return 'dol1';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, selectedTicker, toggleVersion]);

  // Trading activities (from Redis)
  const redisTradingActivities = useMemo(
    () => data ? selectActivityEvents(data) : {},
    [data]
  );

  // Convert Redis activities format to hook format
  const portfolioActivitiesRaw = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, null, null, selectedTimeWindow.key),
    [redisTradingActivities, selectedTimeWindow.key]
  );

  const accountActivities = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, selectedAccountId, null, selectedTimeWindow.key),
    [redisTradingActivities, selectedAccountId, selectedTimeWindow.key]
  );

  const tickerActivities = useMemo(
    () => convertRedisActivitiesToHookFormat(redisTradingActivities, selectedAccountId, selectedTicker, selectedTimeWindow.key),
    [redisTradingActivities, selectedAccountId, selectedTicker, selectedTimeWindow.key]
  );

  // Time selector dropdown (for header)
  const [isTimeSelectorOpen, setIsTimeSelectorOpen] = useState(false);
  const timeSelectorRef = useRef<HTMLDivElement>(null);

  // Sticky time window selector state
  const [showStickyTimeWindow, setShowStickyTimeWindow] = useState(false);
  const [isStickyTimeSelectorOpen, setIsStickyTimeSelectorOpen] = useState(false);
  const stickyTimeSelectorRef = useRef<HTMLDivElement>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);


  // View Portfolio As... tier selection (must be before trading activities hooks)
  // Map 'private' → 'full' for compatibility with getTierVisibilityConfig
  const [viewAsTier, setViewAsTier] = useState<PortfolioViewTier>(() => {
    if (typeof window === 'undefined') return 'full';
    const prefs = loadPortfolioPrefs();
    const portfolioTier = prefs.selfViewTier === 'private' ? 'full' : prefs.selfViewTier;
    return portfolioTier as PortfolioViewTier;
  });

  // Get tier visibility configuration (must be before trading activities hooks)
  const visibilityConfig = useMemo(() => getTierVisibilityConfig(viewAsTier), [viewAsTier]);

  // Listen for changes to vester:portfolio:prefs (synchronized with PortfolioLayout and ViewPortfolioAsModal)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'vester:portfolio:prefs') {
        try {
          const prefs = loadPortfolioPrefs();
          const portfolioTier = prefs.selfViewTier === 'private' ? 'full' : prefs.selfViewTier;
          setViewAsTier(portfolioTier as PortfolioViewTier);
        } catch (error) {
          console.error('Error parsing portfolio prefs from storage event:', error);
        }
      }
    };
    
    const handleCustomEvent = (e: Event) => {
      // Custom event fired from ViewPortfolioAsModal in same window
      try {
        const prefs = loadPortfolioPrefs();
        const portfolioTier = prefs.selfViewTier === 'private' ? 'full' : prefs.selfViewTier;
        setViewAsTier(portfolioTier as PortfolioViewTier);
      } catch (error) {
        console.error('Error parsing portfolio prefs from custom event:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('portfolioPrefsChanged', handleCustomEvent);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('portfolioPrefsChanged', handleCustomEvent);
    };
  }, []);

  // Clear selected account/ticker when tier changes to a restricted tier
  useEffect(() => {
    // Bronze (tier3): Can't see account or ticker data → clear both
    if (viewAsTier === 'tier3' || viewAsTier === 'basic') {
      setSelectedAccountId(null);
      setSelectedTicker(null);
    }
    // Silver (tier2): Can't see individual ticker data → clear ticker only
    else if (viewAsTier === 'tier2') {
      setSelectedTicker(null);
    }
  }, [viewAsTier, setSelectedAccountId, setSelectedTicker]); // Include setters to ensure stable dependency array

  // Conditionally show trading activities based on tier (must be after visibilityConfig is defined)
  const portfolioActivities = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons) {
      return { activities: {} };
    }
    return { activities: portfolioActivitiesRaw };
  }, [portfolioActivitiesRaw, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons]);

  const accountActivitiesFiltered = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons || !selectedAccountId) {
      return { activities: {} };
    }
    return { activities: accountActivities };
  }, [accountActivities, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons, selectedAccountId]);

  const tickerActivitiesFiltered = useMemo(() => {
    if (!visibilityConfig.showChart || visibilityConfig.hideTradingActivityIcons || !selectedAccountId || !selectedTicker) {
      return { activities: {} };
    }
    return { activities: tickerActivities };
  }, [tickerActivities, visibilityConfig.showChart, visibilityConfig.hideTradingActivityIcons, selectedAccountId, selectedTicker]);

  // ========================================================================
  // CUSTOM HOOKS - Modal & CRUD Operations
  // ========================================================================

  // Modal management hook
  const {
    isPortfolioModalOpen,
    portfolioModalView,
    editingAccountId,
    editingTransactionId,
    openPortfolioModal,
    closePortfolioModal,
    setPortfolioModalView,
    setEditingAccountId,
    setEditingTransactionId,
  } = usePortfolioModals();

  // Trading Activity Modal state (separate from portfolio modals)
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedDayActivities, setSelectedDayActivities] = useState<TradingActivity[]>([]);
  const [selectedActivityDate, setSelectedActivityDate] = useState<string>('');

  // Account management hook
  const {
    accounts,
    isDeletingAccount,
    reloadAccountsFromCache,
    invalidateTransactions,
    handleManualAccountSubmit,
    handleUpdateAccountName,
    handleUpdateAccountValue,
    handleUpdateAccountOrder,
    handleDeleteAccountClick,
    handleToggleAccountHiddenInList,
    handleUpdateAccountHidden,
    handleToggleTransactionHidden,
    companyOptions,
  } = useAccountManagement(
    user?.id,
    (accountId: string) => {
      // Callback when account is created
      setEditingAccountId(accountId);
      setEditingTransactionId(null);
      setPortfolioModalView('addTransactionOptions');
    },
    toggleAccountExpansion
  );

  // Transaction management hook
  // Transactions are now managed via React Query, so we just need to invalidate on changes
  const {
    editingTransaction,
    handleManualTransactionSubmit: handleManualTransactionSubmitFromHook,
    handleEditTransactionClick,
    handleDeleteTransaction: handleDeleteTransactionFromHook,
    clearEditingTransaction,
    setEditingTransaction,
  } = useTransactionManagement();

  const transactionRestoreQuery = useAccountTransactions(
    portfolioModalView === 'addManualTransaction' && editingAccountId && editingTransactionId
      ? editingAccountId
      : undefined
  );

  const persistedEditingTransaction = useMemo(() => {
    if (!editingTransactionId || !transactionRestoreQuery.data) return undefined;
    return transactionRestoreQuery.data.find((tx) => tx.id === editingTransactionId);
  }, [editingTransactionId, transactionRestoreQuery.data]);

  useEffect(() => {
    if (
      portfolioModalView === 'addManualTransaction' &&
      editingAccountId &&
      editingTransactionId &&
      !editingTransaction &&
      persistedEditingTransaction
    ) {
      setEditingTransaction(persistedEditingTransaction);
    }
  }, [
    editingAccountId,
    editingTransaction,
    editingTransactionId,
    persistedEditingTransaction,
    portfolioModalView,
    setEditingTransaction,
  ]);

  // Track transaction success message to pass to EditAccountTransactions
  const [transactionSuccessMessage, setTransactionSuccessMessage] = useState<string | null>(null);
  // CSV success message when upload completed from top-level CSV modal (so Edit view can show it)
  const [csvSuccessMessageForEdit, setCsvSuccessMessageForEdit] = useState<string | null>(null);

  // Clear CSV success message when leaving edit account view
  useEffect(() => {
    if (portfolioModalView !== 'editAccountTransactions') {
      setCsvSuccessMessageForEdit(null);
      setLinkSuccessMessageForEdit(null);
    }
  }, [portfolioModalView]);

  // Fetch account directly from Supabase for edit modal (always fresh from database)
  const { data: accountFromDb } = useAccount(
    portfolioModalView === 'editAccountTransactions' && editingAccountId ? editingAccountId : undefined
  );

  // Top-level CSV modal state (lightweight modal without changing background)
  const [showTopLevelCsvModal, setShowTopLevelCsvModal] = useState(false);
  const [showTopLevelSchwabCsvModal, setShowTopLevelSchwabCsvModal] = useState(false);
  const [showTopLevelFidelityCsvModal, setShowTopLevelFidelityCsvModal] = useState(false);
  const [showTopLevelCoinbaseCsvModal, setShowTopLevelCoinbaseCsvModal] = useState(false);
  const [showTopLevelVanguardCsvModal, setShowTopLevelVanguardCsvModal] = useState(false);
  const [showTopLevelDefaultCsvModal, setShowTopLevelDefaultCsvModal] = useState(false);
  const [linkBrokerageModalTitle, setLinkBrokerageModalTitle] = useState('Link Investments');
  const [isLinkBrokerageAccountSetupStep, setIsLinkBrokerageAccountSetupStep] = useState(false);
  const [linkBrokerageResetSignal, setLinkBrokerageResetSignal] = useState(0);
  const [linkBrokerageTargetAccountId, setLinkBrokerageTargetAccountId] = useState<string | null>(null);
  const [linkSuccessMessageForEdit, setLinkSuccessMessageForEdit] = useState<string | null>(null);

  // ========================================================================
  // HELPER FUNCTIONS
  // ========================================================================

  const getDisplayName = () => {
    if (profile?.first_name) {
      return profile.first_name;
    }
    if (user?.email) {
      const namePart = user.email.split('@')[0];
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    }
    return 'User';
  };

  const displayName = getDisplayName();

  // ========================================================================
  // EFFECTS - Persist selections to localStorage
  // ========================================================================

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (selectedTicker != null) {
        localStorage.setItem(LOCALSTORAGE_SELECTED_TICKER, selectedTicker);
      } else {
        localStorage.removeItem(LOCALSTORAGE_SELECTED_TICKER);
      }
    }
  }, [selectedTicker]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (selectedAccountId != null) {
        localStorage.setItem(LOCALSTORAGE_SELECTED_ACCOUNT_ID, selectedAccountId);
      } else {
        localStorage.removeItem(LOCALSTORAGE_SELECTED_ACCOUNT_ID);
      }
    }
  }, [selectedAccountId]);

  // Reset CSV modal when modal view changes or when editingAccountId changes
  // This ensures the CSV upload modal doesn't appear automatically when navigating between modals
  useEffect(() => {
    setShowTopLevelCsvModal(false);
    setShowTopLevelSchwabCsvModal(false);
    setShowTopLevelFidelityCsvModal(false);
    setShowTopLevelCoinbaseCsvModal(false);
    setShowTopLevelVanguardCsvModal(false);
  }, [portfolioModalView, editingAccountId]);

  // Track previous modal view and accountId to detect transitions
  const prevModalViewRef = useRef<PortfolioModalView>(portfolioModalView);
  const prevIsModalOpenRef = useRef<boolean>(isPortfolioModalOpen);
  const prevEditingAccountIdRef = useRef<string | null>(editingAccountId);
  const prevEditingTransactionIdRef = useRef<string | null>(editingTransactionId);

  // Clear manual transaction draft when navigating away from addManualTransaction view
  // This handles Back button and any other navigation away from the form
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const prevView = prevModalViewRef.current;
    const currentView = portfolioModalView;
    const prevAccountId = prevEditingAccountIdRef.current;
    
    // Clear draft when transitioning FROM addManualTransaction TO another view
    // Use previous accountId since current one might already be cleared
    if (prevView === 'addManualTransaction' && currentView !== 'addManualTransaction' && prevAccountId) {
      const storageKey = getFormDraftStorageKey(
        'manualTransaction',
        prevAccountId,
        prevEditingTransactionIdRef.current
      );
      sessionStorage.removeItem(storageKey);
    }
    
    // Update refs for next comparison
    prevModalViewRef.current = currentView;
    prevEditingAccountIdRef.current = editingAccountId;
    prevEditingTransactionIdRef.current = editingTransactionId;
  }, [portfolioModalView, editingAccountId, editingTransactionId]);

  // Clear manual transaction draft when portfolio modal closes while in addManualTransaction view
  // This handles X button click to close the entire modal
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const wasOpen = prevIsModalOpenRef.current;
    const isOpen = isPortfolioModalOpen;
    const prevView = prevModalViewRef.current;
    const prevAccountId = prevEditingAccountIdRef.current;
    
    // Clear draft when modal closes (was open, now closed) while in addManualTransaction view
    // Use previous view and accountId since they might already be cleared/reset
    if (wasOpen && !isOpen && prevView === 'addManualTransaction' && prevAccountId) {
      const storageKey = getFormDraftStorageKey(
        'manualTransaction',
        prevAccountId,
        prevEditingTransactionIdRef.current
      );
      sessionStorage.removeItem(storageKey);
    }
    
    // Update ref for next comparison
    prevIsModalOpenRef.current = isOpen;
  }, [isPortfolioModalOpen, portfolioModalView, editingAccountId]);

  useEffect(() => {
    if (typeof window !== 'undefined' && selectedTimeWindow) {
      localStorage.setItem(LOCALSTORAGE_SELECTED_TIME_WINDOW, selectedTimeWindow.key);
    }
  }, [selectedTimeWindow]);

  // Close time window dropdown when clicking outside (header and sticky)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeSelectorRef.current && !timeSelectorRef.current.contains(event.target as Node)) {
        setIsTimeSelectorOpen(false);
      }
      if (stickyTimeSelectorRef.current && !stickyTimeSelectorRef.current.contains(event.target as Node)) {
        setIsStickyTimeSelectorOpen(false);
      }
    };

    if (isTimeSelectorOpen || isStickyTimeSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimeSelectorOpen, isStickyTimeSelectorOpen]);

  // Scroll listener for sticky time window selector
  // Track the right edge position of the content container for alignment
  const [stickyRightOffset, setStickyRightOffset] = useState(16); // Default to 16px (1rem)
  
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        // Show sticky version when the header has scrolled past the top (with a small buffer)
        setShowStickyTimeWindow(headerRect.bottom < 0);
      }
      
      // Calculate right offset to align with content area
      if (contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const rightEdge = contentRect.right;
        const offset = viewportWidth - rightEdge;
        setStickyRightOffset(Math.max(16, offset)); // Minimum 16px padding
      }
    };

    // Run on scroll and resize
    handleScroll(); // Initial calculation
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);


  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  const handleSelectTicker = useCallback(
    (symbol: string) => {
      if (selectedTicker === symbol) {
        setSelectedTicker(null);
        setSelectedAccountId(null);
      } else {
        setSelectedTicker(symbol);
        setSelectedAccountId(null);
      }
    },
    [selectedTicker]
  );

  const handleSelectAccountTicker = useCallback(
    (acctId: string, ticker: string) => {
      if (selectedTicker === ticker && selectedAccountId === acctId) {
        setSelectedTicker(null);
        setSelectedAccountId(null);
      } else {
        setSelectedTicker(ticker);
        setSelectedAccountId(acctId);
      }
    },
    [selectedTicker, selectedAccountId]
  );

  const handleSelectAccount = useCallback(
    (acctId: string) => {
      if (selectedAccountId === acctId && !selectedTicker) {
        setSelectedAccountId(null);
      } else {
        setSelectedAccountId(acctId);
        setSelectedTicker(null);
      }
    },
    [selectedAccountId, selectedTicker]
  );

  // Activity modal handlers
  const handleActivityMarkerClick = useCallback((dateStr: string, acts: TradingActivity[]) => {
    setSelectedActivityDate(dateStr);
    setSelectedDayActivities(acts);
    setIsActivityModalOpen(true);
  }, []);

  const handleCloseActivityModal = useCallback(() => {
    setIsActivityModalOpen(false);
    setSelectedDayActivities([]);
    setSelectedActivityDate('');
  }, []);

  // Portfolio modal handlers
  const handleEditPortfolio = useCallback(() => {
    try { reloadAccountsFromCache(); } catch {}
    openPortfolioModal();
  }, [openPortfolioModal, reloadAccountsFromCache]);

  const handleAddAccountClick = useCallback(() => {
    setPortfolioModalView('addAccountType');
  }, [setPortfolioModalView]);

  const handleManualAccountClick = useCallback(() => {
    setPortfolioModalView('addManualAccount');
  }, [setPortfolioModalView]);

  const handleLinkAccountClick = useCallback(() => {
    setLinkBrokerageModalTitle('Link Investments');
    setIsLinkBrokerageAccountSetupStep(false);
    setLinkBrokerageTargetAccountId(null);
    setEditingAccountId(null);
    setEditingTransactionId(null);
    setPortfolioModalView('linkBrokerage');
  }, [setEditingAccountId, setEditingTransactionId, setPortfolioModalView]);

  const handleBrokerageAccountsCreated = useCallback(() => {
    reloadAccountsFromCache();
    if (!user?.id) return;

    queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
    queryClient.refetchQueries({ queryKey: ['dashboard', 'self', user.id] });
    queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', user.id] });
  }, [queryClient, reloadAccountsFromCache, user?.id]);

  const handleClosePortfolioModal = useCallback(() => {
    if (portfolioModalView === 'linkBrokerage') {
      try {
        sessionStorage.removeItem(SNAPTRADE_LINK_FLOW_STORAGE_KEY);
      } catch {}
      setLinkBrokerageModalTitle('Link Investments');
      setIsLinkBrokerageAccountSetupStep(false);
      setLinkBrokerageTargetAccountId(null);
    }
    if (
      editingAccountId &&
      ['editAccountTransactions', 'addManualTransaction', 'addTransactionOptions'].includes(portfolioModalView)
    ) {
      clearAccountTransactionFilters(editingAccountId);
    }
    closePortfolioModal();
  }, [closePortfolioModal, editingAccountId, portfolioModalView]);

  const handleGoToAddManualTransaction = useCallback(() => {
    if (!editingAccountId) {
      console.error('[handleGoToAddManualTransaction] Cannot proceed, editingAccountId is null.');
      setPortfolioModalView('list');
      return;
    }
    setEditingTransaction(undefined);
    setEditingTransactionId(null);
    setPortfolioModalView('addManualTransaction');
  }, [editingAccountId, setEditingTransaction, setEditingTransactionId, setPortfolioModalView]);

  const handleUploadCsvClick = useCallback(() => {
    setShowTopLevelCsvModal(true);
  }, []);

  const handleSchwabCsvClick = useCallback(() => {
    setShowTopLevelSchwabCsvModal(true);
  }, []);

  const handleFidelityCsvClick = useCallback(() => {
    setShowTopLevelFidelityCsvModal(true);
  }, []);

  const handleCoinbaseCsvClick = useCallback(() => {
    setShowTopLevelCoinbaseCsvModal(true);
  }, []);

  const handleVanguardCsvClick = useCallback(() => {
    setShowTopLevelVanguardCsvModal(true);
  }, []);

  const handleDefaultCsvClick = useCallback(() => {
    setShowTopLevelDefaultCsvModal(true);
  }, []);

  const handleEditAccountClick = useCallback(
    async (accountId: string) => {
      // Transactions are now loaded via useAccountTransactions hook in EditAccountTransactions
      // No need to pre-fetch them here
      setEditingAccountId(accountId);
      setEditingTransactionId(null);
      setPortfolioModalView('editAccountTransactions');
    },
    [setEditingAccountId, setEditingTransactionId, setPortfolioModalView]
  );

  const handleBackToList = useCallback(() => {
    if (editingAccountId) {
      clearAccountTransactionFilters(editingAccountId);
    }
    setPortfolioModalView('list');
    setEditingAccountId(null);
    setLinkBrokerageTargetAccountId(null);
    setEditingTransactionId(null);
    clearEditingTransaction();
  }, [
    setPortfolioModalView,
    setEditingAccountId,
    setEditingTransactionId,
    clearEditingTransaction,
    editingAccountId,
  ]);

  const handleBackToAccountType = useCallback(() => {
    setPortfolioModalView('addAccountType');
  }, [setPortfolioModalView]);

  const handleBackToLinkBrokerageSelection = useCallback(() => {
    setIsLinkBrokerageAccountSetupStep(false);
    setLinkBrokerageModalTitle('Link Investments');
    setLinkBrokerageResetSignal((current) => current + 1);
  }, []);

  const handleBackFromLinkBrokerage = useCallback(() => {
    try {
      sessionStorage.removeItem(SNAPTRADE_LINK_FLOW_STORAGE_KEY);
    } catch {}
    setLinkBrokerageModalTitle('Link Investments');
    setIsLinkBrokerageAccountSetupStep(false);
    if (linkBrokerageTargetAccountId) {
      setPortfolioModalView('editAccountTransactions');
      return;
    }
    handleBackToAccountType();
  }, [handleBackToAccountType, linkBrokerageTargetAccountId, setPortfolioModalView]);

  const handleManualTransactionCancel = useCallback(() => {
    // Clear draft when navigating away from add transaction form (Back button)
    if (editingAccountId && typeof window !== 'undefined') {
      const storageKey = getFormDraftStorageKey('manualTransaction', editingAccountId, editingTransactionId);
      sessionStorage.removeItem(storageKey);
    }
    setPortfolioModalView('editAccountTransactions');
    setEditingTransactionId(null);
    clearEditingTransaction();
  }, [
    setPortfolioModalView,
    setEditingTransactionId,
    clearEditingTransaction,
    editingAccountId,
    editingTransactionId,
  ]);

  // Modal management: Edit transaction (sets transaction state + switches to form view)
  const handleEditTransactionClickWrapper = useCallback(
    (transaction: ManualTransactionFormData) => {
      // Set transaction state (transaction management)
      handleEditTransactionClick(transaction);
      setEditingTransactionId(transaction.id ?? null);
      // Switch modal view (modal management)
      setPortfolioModalView('addManualTransaction');
    },
    [handleEditTransactionClick, setEditingTransactionId, setPortfolioModalView]
  );

  // Wrapper for transaction submission
  const handleTransactionSubmit = useCallback(
    async (data: any): Promise<TransactionSubmitResult> => {
      if (!editingAccountId) {
        alert('Error: No account selected.');
        return { success: false };
      }
      return await handleManualTransactionSubmitFromHook(data, editingAccountId);
    },
    [editingAccountId, handleManualTransactionSubmitFromHook]
  );

  // Wrapper for transaction deletion
  const handleDeleteTransaction = useCallback(
    async (transactionId: string) => {
      if (!editingAccountId) return;
      await handleDeleteTransactionFromHook(transactionId, editingAccountId);
    },
    [editingAccountId, handleDeleteTransactionFromHook]
  );

  // Wrapper for account deletion that clears selection state
  const handleDeleteAccountClickWrapper = useCallback(
    async (accountId: string) => {
      await handleDeleteAccountClick(accountId);
      
      // Clear selection state if deleted account was selected
      if (selectedAccountId === accountId) {
        setSelectedAccountId(null);
        localStorage.removeItem(LOCALSTORAGE_SELECTED_ACCOUNT_ID);
        // Also clear ticker since the account is gone
        setSelectedTicker(null);
        localStorage.removeItem(LOCALSTORAGE_SELECTED_TICKER);
      }
    },
    [handleDeleteAccountClick, selectedAccountId]
  );

  // Wrapper for account name update
  const handleUpdateAccountNameWrapper = useCallback(
    async (newName: string) => {
      if (!editingAccountId) throw new Error('No account selected');
      await handleUpdateAccountName(editingAccountId, newName);
      // Invalidate account query to refetch from database
      queryClient.invalidateQueries({ queryKey: ['account', editingAccountId] });
    },
    [editingAccountId, handleUpdateAccountName, queryClient]
  );

  // Wrapper for account value update
  const handleUpdateAccountValueWrapper = useCallback(
    async (newValue: number) => {
      if (!editingAccountId) throw new Error('No account selected');
      await handleUpdateAccountValue(editingAccountId, newValue);
      // Invalidate account query to refetch from database
      queryClient.invalidateQueries({ queryKey: ['account', editingAccountId] });
    },
    [editingAccountId, handleUpdateAccountValue, queryClient]
  );

  // Wrapper for account hidden update
  const handleUpdateAccountHiddenWrapper = useCallback(
    async (isHidden: boolean) => {
      if (!editingAccountId) throw new Error('No account selected');
      await handleUpdateAccountHidden(editingAccountId, isHidden);
      // Invalidate account query to refetch from database
      queryClient.invalidateQueries({ queryKey: ['account', editingAccountId] });
    },
    [editingAccountId, handleUpdateAccountHidden, queryClient]
  );

  const handleLinkExistingAccountClick = useCallback(() => {
    if (!editingAccountId) return;
    const targetAccount = accountFromDb || accounts.find((acc: InvestmentAccount) => acc.id === editingAccountId);
    const targetAccountName = targetAccount?.accountName || 'account';
    setLinkBrokerageTargetAccountId(editingAccountId);
    setLinkBrokerageModalTitle(`Link '${targetAccountName}' to External Account`);
    setIsLinkBrokerageAccountSetupStep(false);
    setEditingTransactionId(null);
    setPortfolioModalView('linkBrokerage');
  }, [accountFromDb, accounts, editingAccountId, setEditingTransactionId, setPortfolioModalView]);

  const handleExistingAccountLinked = useCallback(async (accountId: string) => {
    reloadAccountsFromCache();
    queryClient.invalidateQueries({ queryKey: ['account', accountId] });
    queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
      queryClient.refetchQueries({ queryKey: ['dashboard', 'self', user.id] });
      queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', user.id] });
    }
    setEditingAccountId(accountId);
    setEditingTransactionId(null);
    setLinkBrokerageTargetAccountId(null);
    setLinkSuccessMessageForEdit('This account has successfully been linked to your external account!');
    setPortfolioModalView('editAccountTransactions');
  }, [
    queryClient,
    reloadAccountsFromCache,
    setEditingAccountId,
    setEditingTransactionId,
    setPortfolioModalView,
    user?.id,
  ]);

  const handleUnlinkExistingAccount = useCallback(async () => {
    if (!editingAccountId || !session?.access_token) {
      throw new Error('No account or session available');
    }
    await unlinkBrokerageAccount(session.access_token, editingAccountId);
    reloadAccountsFromCache();
    queryClient.invalidateQueries({ queryKey: ['account', editingAccountId] });
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
      queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', user.id] });
    }
  }, [editingAccountId, queryClient, reloadAccountsFromCache, session?.access_token, user?.id]);

  // Wrapper for transaction hidden toggle
  const handleToggleTransactionHiddenWrapper = useCallback(
    async (transactionId: string) => {
      if (!editingAccountId) return;
      await handleToggleTransactionHidden(editingAccountId, transactionId);
    },
    [editingAccountId, handleToggleTransactionHidden]
  );

  // ========================================================================
  // COMPUTED VALUES
  // ========================================================================

  // Determine chart type
  const chartType = useMemo(() => {
    if (selectedTicker) return 'ticker';
    if (selectedAccountId) return 'account';
    return 'portfolio';
  }, [selectedTicker, selectedAccountId]);

  // Format chart data based on type and view
  // When hideToggle is true (tier views), always use percentage data
  const chartData = useMemo(() => {
    // For tier views, always use percentage data (no dollar toggle)
    const forcePctView = visibilityConfig.hideToggle;

    if (chartType === 'ticker') {
      // For ticker charts, we still show price data (not percentage)
      // But we respect the view setting unless hideToggle forces pct
      const view = forcePctView ? 'pct' : tickerView;
      const basisData = tickerCostBasisData as TickerCostBasisPoint[];
      if (view === 'dol2') {
        // dol2 view: Show cumulative PnL data directly from tickerCostBasisData
        return basisData.map((p) => ({
          time: p.time,
          close: p.cumulativeDollarPnl ?? 0,  // Use cumulative PnL as the value
          basis: p.basis
        }));
      } else {
        // dol1 or pct view: Show price data (existing behavior)
        let i = 0;
        let lastBasis: number | null = null;
        const merged = tickerChart.priceSeries.map((p) => {
          const t = p.time;
          while (i < basisData.length && basisData[i].time <= t) {
            lastBasis = basisData[i].basis as number | null;
            i += 1;
          }
          return { time: p.time, close: p.close, basis: lastBasis };
        });
        return merged;
      }
    } else if (chartType === 'account') {
      const view = forcePctView ? 'pct' : accountChart.view;
      if (view === 'dollar') {
        // $ view: Show dollar data
        return accountChart.series.map((p) => ({ 
          time: p.time, 
          percent: p.dollar ?? 0,  // Use dollar value, but keep key as 'percent' for chart compatibility
          dollar: p.dollar 
        }));
      } else {
        // % view: Show percentage data
        return accountChart.series.map((p) => ({ time: p.time, percent: p.percent }));
      }
    } else {
      // Use portfolioChart from Redis
      const view = forcePctView ? 'pct' : portfolioChart.view;
      if (view === 'dollar') {
        // $ view: Show dollar data
        return portfolioChart.series.map((p) => ({ 
          time: p.time, 
          percent: p.dollar ?? 0,  // Use dollar value, but keep key as 'percent' for chart compatibility
          dollar: p.dollar 
        }));
      } else {
        // % view: Show percentage data
        return portfolioChart.series.map((p) => ({ time: p.time, percent: p.percent }));
      }
    }
  }, [chartType, tickerChart, accountChart, portfolioChart, tickerCostBasisData, tickerView, visibilityConfig.hideToggle]);

  // Compute loading state for chart based on chart type
  // For ticker charts, check lazy-loading states; for account/portfolio, use dashboard fetch state
  const isChartFetching = useMemo(() => {
    if (chartType === 'ticker') {
      // Ticker charts use lazy-loaded endpoints, so check their loading states
      return isLoadingTickerChart || isLoadingUserTickerChart;
    } else {
      // Account and portfolio charts come from dashboard data
      return dashboardData.isFetching;
    }
  }, [chartType, isLoadingTickerChart, isLoadingUserTickerChart, dashboardData.isFetching]);

  // Chart title
  const chartTitle = useMemo(() => {
    // Helper to format dollar values with commas
    const formatDollar = (value: number): string => {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (chartType === 'ticker' && selectedTicker) {
      // Get company name and price from ticker market data (vester:ticker:market:AAL cache)
      // Backend returns: {data: {ticker: "AAL", company_name: "...", last_price: 15.99, ...}, meta: {...}}
      // For single ticker response, data IS the TickerMarketData object (not nested)
      const marketData = selectedTickerMarketData?.data;
      // Check if it's a single ticker response (has company_name directly) vs batch (has tickers property)
      const isSingleTicker = marketData && 'company_name' in marketData;
      const tickerMarketData = isSingleTicker ? (marketData as any) : null;
      
      const companyName = tickerMarketData?.company_name || tickerChart.name || selectedTicker;
      // Get price from market data (last_price from vester:ticker:market:AAL cache)
      const currentPrice = tickerMarketData?.last_price;
      
      const view = tickerView;
      const basisData = tickerCostBasisData as TickerCostBasisPoint[];
      
      if (view === 'dol2') {
        // dol2 view: Show latest cumulative PnL
        const latestPnl = basisData.length > 0 
          ? basisData[basisData.length - 1].cumulativeDollarPnl 
          : undefined;
        const pnlText = typeof latestPnl === 'number' 
          ? `(${latestPnl >= 0 ? '+' : '-'}$${formatDollar(Math.abs(latestPnl))})` 
          : '';
        return `${companyName} (${selectedTicker}) ${pnlText}`;
      } else {
        // dol1/pct view: Show company name, ticker, and current price
        // Format: "American Airlines (AAL) - $15.99"
        // Use last_price from market data, fallback to last chart point if market data not loaded yet
        let displayPrice: number | null = null;
        if (typeof currentPrice === 'number' && !isNaN(currentPrice) && currentPrice > 0) {
          displayPrice = currentPrice;
        } else if (tickerChart.priceSeries.length > 0) {
          const lastPoint = tickerChart.priceSeries[tickerChart.priceSeries.length - 1];
          if (typeof lastPoint?.close === 'number' && !isNaN(lastPoint.close) && lastPoint.close > 0) {
            displayPrice = lastPoint.close;
          }
        }
        
        const priceText = displayPrice !== null
          ? ` - $${formatDollar(displayPrice)}` 
          : '';
        return `${companyName} (${selectedTicker})${priceText}`;
      }
    }

    if (chartType === 'account' && selectedAccountId) {
      const acc = accounts.find((a: InvestmentAccount) => a.id === selectedAccountId);
      const view = accountChart.view;
      if (view === 'dollar') {
        // $ view: Show current account value
        const dollar = accountChart.headerDollar;
        const dollarText = typeof dollar === 'number' ? `($${formatDollar(dollar)})` : '';
        return `${acc?.accountName || 'Account'} ${dollarText}`;
      } else {
        // % view: Show percentage change (existing behavior)
        const pct = accountChart.headerPct;
        const pctText = typeof pct === 'number' ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
        return `${acc?.accountName || 'Account'}${pctText}`;
      }
    }

    // Use portfolioChart from Redis
    const view = portfolioChart.view;
    if (view === 'dollar') {
      // $ view: Show current portfolio value
      const dollar = portfolioChart.headerDollar;
      const dollarText = typeof dollar === 'number' ? `($${formatDollar(dollar)})` : '';
      return `My Portfolio ${dollarText}`;
    } else {
      // % view: Show percentage change (existing behavior)
      const pPct = portfolioChart.headerPct;
      const pPctText = typeof pPct === 'number' ? ` (${pPct >= 0 ? '+' : ''}${pPct.toFixed(1)}%)` : '';
      return `My Portfolio${pPctText}`;
    }
  }, [chartType, selectedTicker, selectedAccountId, tickerChart.name, tickerChart.priceSeries, accounts, accountChart.headerPct, accountChart.headerDollar, accountChart.view, portfolioChart.headerPct, portfolioChart.headerDollar, portfolioChart.view, tickerView, tickerCostBasisData, selectedTickerMarketData]);

  // Grouping for trading activities
  const groupingMs = useMemo(() => {
    // Only group into weeks for 1 year or 2 year views
    if (selectedTimeWindow.key === '1y' || selectedTimeWindow.key === '2y') {
      return 7 * 24 * 60 * 60 * 1000; // one week
    }
    // Everything else: no grouping (use exact date matching)
    return 0;
  }, [selectedTimeWindow.key]);

  // Format time for chart axis
  const formatTime = useCallback(
    (ms: number) => {
      const d = new Date(ms);
      if (selectedTimeWindow.key === '1m' || selectedTimeWindow.key === '3m') {
        return `${d.getMonth() + 1}/${d.getDate()}`;
      }
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },
    [selectedTimeWindow.key]
  );

  // ========================================================================
  // RENDER
  // ========================================================================

  // Get tier indicator for welcome header
  const tierIndicator = useMemo(() => {
    if (viewAsTier === 'full') return null;
    
    const tierInfo = {
      tier1: { label: 'Gold view', color: 'text-yellow-500' },
      tier2: { label: 'Silver view', color: 'text-gray-400' },
      tier3: { label: 'Bronze view', color: '' }, // Custom color #A75217
      basic: { label: 'Basic view', color: 'text-gray-300' },
    }[viewAsTier];

    return tierInfo;
  }, [viewAsTier]);

  // Determine if we should show any cards (for Basic tier)
  const shouldShowAnyCards =
    visibilityConfig.showPortfolioSummary ||
    visibilityConfig.showChart ||
    visibilityConfig.showInvestmentTable ||
    visibilityConfig.showTopMovers;

  // Render cards based on tier
  const renderCards = () => {
    // Basic tier: Hide all cards
    if (!shouldShowAnyCards) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>You are viewing your portfolio in Basic mode. All portfolio data is hidden.</p>
        </div>
      );
    }

    // Determine layout based on tier
    const isTier3Layout = visibilityConfig.chartAndTableLayout === 'fullWidth';
    const isTier2Layout = visibilityConfig.chartAndTableLayout === 'sideBySide';
    const isTier1Layout = viewAsTier === 'tier1';
    const isFullLayout = viewAsTier === 'full';

    // Tier 1 and Full use the standard 3-column grid layout
    if (isTier1Layout || isFullLayout) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Portfolio Summary Card */}
          {visibilityConfig.showPortfolioSummary && (
            <div className="md:col-span-2">
              <PortfolioSummaryCard
                className="h-full"
                onEditClick={handleEditPortfolio}
                hideDollarSection={visibilityConfig.hideToggle}
              />
            </div>
          )}

          {/* Top Movers Card */}
          {visibilityConfig.showTopMovers && (
            <div>
              <TopMoversCard
                className="h-full"
                selectedTicker={selectedTicker}
                onSelectTicker={handleSelectTicker}
                hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
              />
            </div>
          )}

          {/* Performance Chart Card */}
          {visibilityConfig.showChart && (
            <div className="md:col-span-2">
              <PerformanceChartCard
                className="h-full"
                chartType={chartType}
                chartData={chartData}
                title={chartTitle}
                selectedTicker={selectedTicker}
                selectedAccountId={selectedAccountId}
                timeWindowKey={selectedTimeWindow.key}
                view={(
                  visibilityConfig.hideToggle 
                    ? "pct"
                    : chartType === 'ticker' 
                      ? tickerView
                      : chartType === 'account' 
                        ? accountChart.view 
                        : portfolioChart.view
                ) as 'pct' | 'dollar' | 'dol1' | 'dol2'}
                portfolioActivities={portfolioActivities.activities}
                accountActivities={accountActivitiesFiltered.activities}
                tickerActivities={tickerActivitiesFiltered.activities}
                groupingMs={groupingMs}
                onActivityMarkerClick={handleActivityMarkerClick}
                formatTime={formatTime}
                isFetching={isChartFetching}
                hideToggle={visibilityConfig.hideToggle}
              />
            </div>
          )}

          {/* Investment Table */}
          {visibilityConfig.showInvestmentTable && (
            <div>
              <InvestmentTableCard
                className="h-full"
                selectedTicker={selectedTicker}
                selectedAccountId={selectedAccountId}
                expandedAccounts={expandedAccounts}
                onSelectAccountTicker={handleSelectAccountTicker}
                onSelectAccount={handleSelectAccount}
                onToggleAccountExpansion={toggleAccountExpansion}
                onEditPortfolio={handleEditPortfolio}
                hideShares={visibilityConfig.hideShares}
                showMyTotalText={visibilityConfig.showMyTotalText}
                hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
                enableAccountExpansion={visibilityConfig.enableAccountExpansion} // Hide expander arrows for tiers without ticker visibility
              />
            </div>
          )}
        </div>
      );
    }

    // Tier 2 and Tier 3 use custom layouts
    return (
      <div
        className={`grid gap-4 ${
          isTier3Layout
            ? 'grid-cols-1'
            : isTier2Layout
            ? 'grid-cols-1 md:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-3'
        }`}
      >
        {/* Portfolio Summary Card */}
        {visibilityConfig.showPortfolioSummary && (
          <div className={visibilityConfig.portfolioSummaryFullWidth ? 'md:col-span-3' : ''}>
            <PortfolioSummaryCard
              className="h-full"
              onEditClick={handleEditPortfolio}
              hideDollarSection={true}
            />
          </div>
        )}

        {/* Top Movers Card - not shown for Tier 2/3 */}
        {visibilityConfig.showTopMovers && (
          <div className={isTier2Layout ? 'md:col-span-1' : ''}>
            <TopMoversCard
              className="h-full"
              selectedTicker={selectedTicker}
              onSelectTicker={handleSelectTicker}
              hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
            />
          </div>
        )}

        {/* Performance Chart Card */}
        {visibilityConfig.showChart && (
          <div
            className={
              isTier2Layout
                ? 'md:col-span-2'
                : isTier3Layout
                ? 'md:col-span-3'
                : ''
            }
          >
            <PerformanceChartCard
              className="h-full"
              chartType={chartType}
              chartData={chartData}
              title={chartTitle}
              selectedTicker={selectedTicker}
              selectedAccountId={selectedAccountId}
              timeWindowKey={selectedTimeWindow.key}
              view="pct"
              portfolioActivities={portfolioActivities.activities}
              accountActivities={accountActivitiesFiltered.activities}
              tickerActivities={tickerActivitiesFiltered.activities}
              groupingMs={groupingMs}
              onActivityMarkerClick={handleActivityMarkerClick}
              formatTime={formatTime}
              isFetching={isChartFetching}
              hideToggle={visibilityConfig.hideToggle}
            />
          </div>
        )}

        {/* Investment Table */}
        {visibilityConfig.showInvestmentTable && (
          <div className={isTier2Layout ? 'md:col-span-1' : ''}>
            <InvestmentTableCard
              className="h-full"
              selectedTicker={selectedTicker}
              selectedAccountId={selectedAccountId}
              expandedAccounts={expandedAccounts}
              onSelectAccountTicker={handleSelectAccountTicker}
              onSelectAccount={handleSelectAccount}
              onToggleAccountExpansion={toggleAccountExpansion}
              onEditPortfolio={handleEditPortfolio}
              hideShares={visibilityConfig.hideShares}
              showMyTotalText={visibilityConfig.showMyTotalText}
              hideShowRealizedGains={visibilityConfig.hideShowRealizedGains}
              enableAccountExpansion={visibilityConfig.enableAccountExpansion} // Hide expander arrows for tiers without ticker visibility
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={contentRef} className="space-y-3">
      {/* Welcome Header and Time Window Selector */}
      <div ref={headerRef} className="flex justify-between items-center -mt-4 mb-2">
        <h1 className="text-lg text-gray-800">
          Welcome, {displayName}
          {tierIndicator && (
            <span className="ml-2">
              (<span className="flex items-center space-x-1 inline-flex">
                <span 
                  className={`${tierIndicator.color || ''} text-base`}
                  style={viewAsTier === 'tier3' ? { color: '#A75217' } : undefined}
                >
                  ★
                </span>
                <span className="text-gray-600">{tierIndicator.label}</span>
              </span>)
            </span>
          )}
        </h1>
        <div className="flex items-center space-x-2 relative">
          {/* Time Window Selector - Only show if tier allows */}
          {visibilityConfig.showTimePicker && (
            <div className="relative" ref={timeSelectorRef}>
              <button
                onClick={() => setIsTimeSelectorOpen(!isTimeSelectorOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{selectedTimeWindow.label}</span>
              </button>
              {/* Time Window Dropdown */}
              {isTimeSelectorOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 top-full">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {timeWindowOptions.map((option) => (
                      <button
                        key={option.key}
                        onClick={() => {
                          setSelectedTimeWindow(option);
                          setIsTimeSelectorOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${ 
                          selectedTimeWindow.key === option.key
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700'
                        } hover:bg-gray-100 hover:text-gray-900`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Time Window Selector (appears when scrolled) */}
      {showStickyTimeWindow && visibilityConfig.showTimePicker && (
        <div 
          className="fixed top-4 z-50 transition-all duration-200"
          style={{ right: `${stickyRightOffset}px` }}
        >
          <div className="relative" ref={stickyTimeSelectorRef}>
            <button
              onClick={() => setIsStickyTimeSelectorOpen(!isStickyTimeSelectorOpen)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{selectedTimeWindow.label}</span>
            </button>
            
            {/* Time Window Dropdown */}
            {isStickyTimeSelectorOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 top-full">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {timeWindowOptions.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => {
                        setSelectedTimeWindow(option);
                        setIsStickyTimeSelectorOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm ${ 
                        selectedTimeWindow.key === option.key
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700'
                      } hover:bg-gray-100 hover:text-gray-900`}
                      role="menuitem"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cards Grid */}
      {isLoading && !data ? (
        // Only show skeleton on initial load (no cached data)
        <PortfolioPageSkeleton />
      ) : (
        <>
          {/* Show error chip if there's an error but we have cached data */}
          {error && data && (
            <ReconnectingChip 
              message={error instanceof Error ? error.message : 'Failed to refresh portfolio data'} 
              onRetry={refetch} 
            />
          )}
          {renderCards()}
        </>
      )}

      {/* Trading Activity Modal */}
      <TradingActivityModal
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
        activities={selectedDayActivities}
        date={selectedActivityDate}
        isWeeklyView={selectedTimeWindow.key === '1y' || selectedTimeWindow.key === '2y'}
        friendView={visibilityConfig.tradingActivityFriendView}
      />

      {/* Portfolio Edit Modal */}
      <Modal
        isOpen={isPortfolioModalOpen}
        onClose={handleClosePortfolioModal}
        title={getModalTitle()}
        showDoneButton={portfolioModalView === 'list'}
        onBackClick={portfolioModalView !== 'list' ? getBackHandler() : undefined}
        bodyScrollResetKey={`${portfolioModalView}:${editingAccountId ?? ''}`}
        size={
          portfolioModalView === 'editAccountTransactions' || portfolioModalView === 'list'
            ? '4xl'
            : 'xl'
        }
      >
        {renderModalContent()}
      </Modal>

      {/* Top-level CSV Upload Modals */}
      {showTopLevelCsvModal && editingAccountId && (
        <BrokerCsvUploadModal
          isOpen={showTopLevelCsvModal}
          accountId={editingAccountId}
          broker="etrade"
          onClose={() => setShowTopLevelCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
      {showTopLevelFidelityCsvModal && editingAccountId && (
        <BrokerCsvUploadModal
          isOpen={showTopLevelFidelityCsvModal}
          accountId={editingAccountId}
          broker="fidelity"
          onClose={() => setShowTopLevelFidelityCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
      {showTopLevelSchwabCsvModal && editingAccountId && (
        <BrokerCsvUploadModal
          isOpen={showTopLevelSchwabCsvModal}
          accountId={editingAccountId}
          broker="schwab"
          onClose={() => setShowTopLevelSchwabCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
      {showTopLevelCoinbaseCsvModal && editingAccountId && (
        <BrokerCsvUploadModal
          isOpen={showTopLevelCoinbaseCsvModal}
          accountId={editingAccountId}
          broker="coinbase"
          onClose={() => setShowTopLevelCoinbaseCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
      {showTopLevelVanguardCsvModal && editingAccountId && (
        <BrokerCsvUploadModal
          isOpen={showTopLevelVanguardCsvModal}
          accountId={editingAccountId}
          broker="vanguard"
          onClose={() => setShowTopLevelVanguardCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
      {showTopLevelDefaultCsvModal && editingAccountId && (
        <DefaultCsvUploadModal
          isOpen={showTopLevelDefaultCsvModal}
          accountId={editingAccountId}
          onClose={() => setShowTopLevelDefaultCsvModal(false)}
          autoCloseOnSuccess
          onRefreshImpacted2={async (acctId) => {
            invalidateTransactions(acctId);
            setEditingTransactionId(null);
            setPortfolioModalView('editAccountTransactions');
          }}
          onSuccess={(hints) => {
            const newTickers = hints?.newTickers;
            setCsvSuccessMessageForEdit(
              newTickers?.length
                ? `New ticker(s) (${newTickers.join(', ')}) are being added to the backend. Please allow a little time before your portfolio is updated accordingly.`
                : 'CSV uploaded successfully!'
            );
          }}
        />
      )}
    </div>
  );

  // ========================================================================
  // MODAL HELPER FUNCTIONS
  // ========================================================================

  function getModalTitle(): string {
    let accountName = 'Account';
    if (editingAccountId) {
      const account = accounts.find((acc: InvestmentAccount) => acc.id === editingAccountId);
      if (account) {
        accountName = account.accountName;
      }
    }

    switch (portfolioModalView) {
      case 'list':
        return 'Manage Accounts';
      case 'addAccountType':
        return 'Add Account';
      case 'addManualAccount':
        return 'Add Manual Account';
      case 'linkBrokerage':
        return linkBrokerageModalTitle;
      case 'addTransactionOptions':
        return `Add Transactions for ${accountName}`;
      case 'editAccountTransactions':
        return `Edit account - ${accountName}`;
      case 'addManualTransaction':
        return editingTransaction || editingTransactionId
          ? `Edit transaction in ${accountName}`
          : `Add transaction to ${accountName}`;
      default:
        return 'Portfolio';
    }
  }

  function getBackHandler(): (() => void) {
    switch (portfolioModalView) {
      case 'addManualAccount':
        return handleBackToAccountType;
      case 'linkBrokerage':
        return isLinkBrokerageAccountSetupStep
          ? handleBackToLinkBrokerageSelection
          : handleBackFromLinkBrokerage;
      case 'addManualTransaction':
        return handleManualTransactionCancel;
      case 'editAccountTransactions':
        return handleBackToList;
      case 'addAccountType':
        return handleBackToList;
      case 'addTransactionOptions':
        return handleBackToList;
      default:
        return closePortfolioModal;
    }
  }

  function renderModalContent(): React.ReactNode {
    // For edit modal, use account from database (always fresh)
    // with the cached account as a fallback so transient refetches do not blank the modal.
    const cachedAccount = accounts.find((acc: InvestmentAccount) => acc.id === editingAccountId);
    const matchingAccountFromDb = accountFromDb?.id === editingAccountId ? accountFromDb : undefined;
    const accountToEdit = portfolioModalView === 'editAccountTransactions'
      ? matchingAccountFromDb ?? cachedAccount
      : cachedAccount;
    const existingNames = accounts.map((acc: InvestmentAccount) => acc.accountName);
    const linkTargetAccount = linkBrokerageTargetAccountId
      ? accounts.find((acc: InvestmentAccount) => acc.id === linkBrokerageTargetAccountId)
        || (accountFromDb?.id === linkBrokerageTargetAccountId ? accountFromDb : null)
      : null;
    const linkTargetCompanyOption = linkTargetAccount?.company
      ? companyOptions.find((option) => option.value === linkTargetAccount.company)
      : null;

    const withDeletingOverlay = (content: React.ReactNode) => (
      <div className="relative">
        {content}
        {isDeletingAccount && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <div className="text-lg font-medium">Deleting account...</div>
          </div>
        )}
      </div>
    );

    const restoreTransactionContent = () => {
      if (!editingTransactionId || editingTransaction || persistedEditingTransaction) return null;

      if (transactionRestoreQuery.isLoading || transactionRestoreQuery.isFetching) {
        return (
          <div className="flex flex-col items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <div className="text-lg font-medium">Loading transaction...</div>
          </div>
        );
      }

      if (transactionRestoreQuery.isError) {
        return (
          <div className="space-y-4 p-6 text-center">
            <div>
              <div className="text-lg font-medium">Could not load transaction.</div>
              <div className="mt-1 text-sm text-gray-500">Please retry or return to the transaction list.</div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => transactionRestoreQuery.refetch()}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={handleManualTransactionCancel}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-3 p-6 text-center">
          <div className="text-lg font-medium">Transaction not found.</div>
          <button
            type="button"
            onClick={handleManualTransactionCancel}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      );
    };

    const accountLoadingContent = (
      <div className="flex flex-col items-center justify-center p-10">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-lg font-medium">Loading account...</div>
      </div>
    );

    const accountNotFoundContent = (
      <div className="space-y-3 p-6 text-center">
        <div className="text-lg font-medium">Account not found.</div>
        <button
          type="button"
          onClick={handleBackToList}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Back
        </button>
      </div>
    );

    switch (portfolioModalView) {
      case 'list':
        return withDeletingOverlay(
          <AccountList
            accounts={accounts}
            onAddAccountClick={handleAddAccountClick}
            onEditAccountClick={handleEditAccountClick}
            onDeleteAccountClick={handleDeleteAccountClickWrapper}
            onToggleHiddenClick={handleToggleAccountHiddenInList}
            onReorderAccounts={handleUpdateAccountOrder}
          />
        );
      case 'addAccountType':
        return withDeletingOverlay(
          <AddAccountType
            onManualClick={handleManualAccountClick}
            onLinkClick={handleLinkAccountClick}
            isPremium={profile?.is_premium === true}
          />
        );
      case 'linkBrokerage':
        return withDeletingOverlay(
          <LinkBrokerageModal
            token={session?.access_token}
            existingAccountNames={existingNames}
            mode={linkBrokerageTargetAccountId ? 'link-existing-account' : 'create-accounts'}
            targetAccountId={linkBrokerageTargetAccountId}
            targetAccountName={linkTargetAccount?.accountName ?? null}
            targetAccountCompany={linkTargetAccount?.company ?? null}
            targetAccountCompanyLabel={linkTargetCompanyOption?.label ?? null}
            onAccountsCreated={handleBrokerageAccountsCreated}
            onExistingAccountLinked={handleExistingAccountLinked}
            onCancel={getBackHandler()}
            onClose={handleClosePortfolioModal}
            onStepChange={setIsLinkBrokerageAccountSetupStep}
            onTitleChange={setLinkBrokerageModalTitle}
            resetToBrokerSelectionSignal={linkBrokerageResetSignal}
          />
        );
      case 'addManualAccount':
        return withDeletingOverlay(
          <AddManualAccountForm
            onSubmit={handleManualAccountSubmit}
            onBackClick={getBackHandler()}
            existingAccountNames={existingNames}
            companyOptions={companyOptions}
          />
        );
      case 'addTransactionOptions':
        return withDeletingOverlay(
          <AddTransactionOptions
            accountName={accountToEdit?.accountName || 'Account'}
            company={accountToEdit?.company || ''}
            onManualClick={handleGoToAddManualTransaction}
            onUploadClick={handleUploadCsvClick}
            onSchwabUploadClick={handleSchwabCsvClick}
            onFidelityUploadClick={handleFidelityCsvClick}
            onCoinbaseUploadClick={handleCoinbaseCsvClick}
            onVanguardUploadClick={handleVanguardCsvClick}
            onDefaultCsvClick={handleDefaultCsvClick}
          />
        );
      case 'editAccountTransactions':
        if (!accountToEdit && editingAccountId) {
          return withDeletingOverlay(accountLoadingContent);
        }

        if (!accountToEdit) {
          return withDeletingOverlay(accountNotFoundContent);
        }

        const otherAccountNames = accounts
          .filter((acc: InvestmentAccount) => acc.id !== editingAccountId)
          .map((acc: InvestmentAccount) => acc.accountName);

        return withDeletingOverlay(
          <EditAccountTransactions
            account={accountToEdit}
            onBack={handleBackToList}
            onUpdateAccountName={handleUpdateAccountNameWrapper}
            existingAccountNames={otherAccountNames}
            onAddTransactionClick={handleGoToAddManualTransaction}
            onToggleTransactionHidden={handleToggleTransactionHiddenWrapper}
            onEditTransactionClick={handleEditTransactionClickWrapper}
            onDeleteTransaction={handleDeleteTransaction}
            onUpdateAccountHidden={handleUpdateAccountHiddenWrapper}
            onUpdateAccountValue={handleUpdateAccountValueWrapper}
            onLinkAccountClick={handleLinkExistingAccountClick}
            onUnlinkAccount={handleUnlinkExistingAccount}
            onRefreshImpacted2={async (acctId) => {
              invalidateTransactions(acctId);
            }}
            initialCsvSuccessMessage={csvSuccessMessageForEdit}
            initialTransactionSuccessMessage={transactionSuccessMessage}
            initialLinkSuccessMessage={linkSuccessMessageForEdit}
          />
        );
      case 'addManualTransaction': {
        const restoreContent = restoreTransactionContent();
        if (!editingAccountId) return null;
        if (restoreContent) return withDeletingOverlay(restoreContent);

        return withDeletingOverlay(
          <ManualTransactionForm
            onSubmit={handleTransactionSubmit}
            onCancel={handleManualTransactionCancel}
            initialData={editingTransaction ?? persistedEditingTransaction}
            accountId={editingAccountId}
            onSaved={({ isEdit, addAnother, successMessage }) => {
              if (isEdit) {
                invalidateTransactions(editingAccountId);
                return;
              }
              if (!addAnother) {
                invalidateTransactions(editingAccountId);
                // Set success message to show in Edit Account modal
                if (successMessage) {
                  setTransactionSuccessMessage(successMessage);
                  // Clear message after it's been displayed (component will auto-dismiss after 10s)
                  setTimeout(() => setTransactionSuccessMessage(null), 11000);
                }
              }
            }}
            onClosed={({ didSaveDuringSession }) => {
              if (didSaveDuringSession) {
                invalidateTransactions(editingAccountId);
              }
            }}
          />
        );
      }
      default:
        return null;
    }
  }
};

export default PortfolioPage;
